import React, { useEffect, useState, useRef } from 'react';
import { View, Pressable, Text, StyleSheet, Alert, Platform, Animated } from 'react-native';
import * as WebBrowser from "expo-web-browser";
import { makeRedirectUri, ResponseType, useAuthRequest, useAutoDiscovery } from 'expo-auth-session';
import { LinearGradient } from 'expo-linear-gradient';
import LottieView from 'lottie-react-native';
import { createURL } from 'expo-linking';
import apis from '@api/api';
import { useDispatch, useSelector } from 'react-redux';
import { ADD_INTEGRATION, HIDE_LOADING_MODAL, SHOW_AUTH_MODAL, SHOW_LOADING_MODAL, SHOW_OTP, SHOW_TC_MODAL, SUCCESS } from '@reduxLocal/actions/types';
import { DefaultRootState } from '@reduxLocal/persistState';
import { useNavigation } from '@react-navigation/native';
import {
  handleError,
  handleUserData,
  handleTermsAndConditions,
  handleOAuthResponse,
  createOAuthUser,
  handleOAuthLogin,
  fetchUserInfoFromProvider,
  OAuthUser
} from './utils/authUtils';
import { getAuthSessionGuard } from '@utils/authSessionGuard';

// This should be called at the root of your app (e.g., App.js)
// to ensure the auth session can be completed properly.
WebBrowser.maybeCompleteAuthSession();

// Get auth session guard singleton
const authGuard = getAuthSessionGuard();



/**
 * A button component to handle LinkedIn authentication using Expo's AuthSession.
 * It manages the entire authentication flow, from prompting the user to sign in,
 * to fetching their user information from the LinkedIn API.
 */
const LinkedInButton = ({ code, productId }: { code?: string; productId?: string }) => {
  // State to hold the access token from LinkedIn.
  const [token, setToken] = useState(null);
  // State to hold the user's information fetched from LinkedIn.
  const [userInfo, setUserInfo] = useState(null);
  // Animation for code indicator
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const notificationToken: string = useSelector(
    (state: DefaultRootState) => state.app.notificationToken
  );
  const dispatch = useDispatch();
  const navigation = useNavigation();

  // Animation effect for code indicator
  useEffect(() => {
    if (code) {
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 1000,
        useNativeDriver: true,
      }).start();
    }
  }, [code, fadeAnim]);

  // Log state changes
  useEffect(() => {
    console.log('Token state changed:', token);
  }, [token]);

  useEffect(() => {
    console.log('UserInfo state changed:', userInfo);
  }, [userInfo]);

  // Automatically discover LinkedIn's OAuth2 endpoints.
  const discovery = { authorizationEndpoint: 'https://www.linkedin.com/oauth/v2/authorization' };

  // Set up the authentication request hook.
  const [request, response, promptAsync] = useAuthRequest(
    {
      clientId: process.env.EXPO_PUBLIC_LINKEDIN_CLIENTID,
      clientSecret: process.env.EXPO_PUBLIC_LINKEDIN_CLIENTSECRET,
      // LinkedIn OAuth scopes:
      // - 'openid', 'profile', 'email': Basic profile information via OpenID Connect
      // - 'w_member_social': Post to personal profile
      // - 'r_organization_social': Read organization posts
      // - 'rw_organization_admin': Manage organization pages
      // - 'w_organization_social': Post to organization pages
      // - 'r_1st_connections_size': Get 1st-degree connections count
      scopes: [
        'openid', 
        'profile', 
        'email', 
        'w_member_social',
        'r_organization_social',
        'rw_organization_admin',
        'w_organization_social',
        'r_1st_connections_size'
      ],
      // `makeRedirectUri` is the recommended way to create redirect URIs.
      redirectUri: Platform.select({ web: createURL('/universal/integrations'), default: `https://api.smartsaas.pro/app-redirect` }),
      responseType: ResponseType.Code,
    },
    discovery
  );

  /**
   * This effect hook handles the response from the LinkedIn authentication prompt.
   */
  useEffect(() => {
    if (response) {
      console.log('LinkedIn Auth Response:', response);
      if (response.type === 'success') {
        const { code, access_token } = response.params;
        console.log('Authorization Code:', code);
        console.log('Access Token (if available):', access_token);

        // LinkedIn OAuth2 typically returns a code, not an access token directly
        if (access_token) {
          console.log('Setting access token:', access_token);
          setToken(access_token);
        } else if (code) {
          console.log('Received authorization code, need to exchange for access token:', code);
          // Send the code to your backend to exchange for an access token
          const handleLinkedInRegistration = async () => {
            try {
              console.log('Received authorization code, exchanging for access token:', code);
              const res = await apis.registerLinkedIn(code, Platform.select({ web: createURL('/universal/integrations'), default: `https://api.smartsaas.pro/app-redirect` }));
              console.log('LinkedIn registration response:', res);
              await handleOAuthResponse(res, dispatch, navigation, notificationToken, 'LinkedIn');
            } catch (error) {
              console.error('LinkedIn registration error:', error);
              handleError(dispatch, 'Failed to connect LinkedIn account. Please try again.');
            }
          };
          
          handleLinkedInRegistration();
        }
      } else if (response.type === 'error') {
        console.error('LinkedIn Auth Error:', response);
        Alert.alert('Authentication Error', response.params.error_description || 'An unknown error occurred.');
      } else if (response.type === 'dismiss' || response.type === 'cancel') {
        console.log('LinkedIn authentication was dismissed/cancelled');
      }
    }
  }, [response]);

  /**
   * This effect hook fetches the user's information from the LinkedIn API
   * once an access token has been successfully obtained.
   */
  useEffect(() => {
    if (!token) return;

    console.log('Fetching user info with token:', token);

    const fetchUserInfo = async () => {
      try {
        await fetchUserInfoFromProvider(
          'https://api.linkedin.com/v2/userinfo',
          token,
          setUserInfo,
          'LinkedIn'
        );
      } catch (error) {
        handleError(dispatch, 'An error occurred while fetching LinkedIn user data.');
      }
    };

    fetchUserInfo();
  }, [token]);

  const handleClick = async () => {
    // Check if another auth session is active
    if (authGuard.isSessionActive()) {
      const activeSession = authGuard.getActiveSession();
      console.warn(`Cannot start LinkedIn auth - ${activeSession} is already active`);
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: {
          content: `Please wait for ${activeSession} authentication to complete`,
          type: 'error'
        }
      });
      return;
    }

    console.log('LinkedIn button pressed, starting authentication...');
    console.log('Request details:', request);

    // Start auth session with guard
    const result = await authGuard.startSession('LinkedIn', async () => {
      return await promptAsync();
    });

    console.log('PromptAsync result:', result);
    if (result?.type === 'success') {
      console.log('Authentication successful:', result);
    } else if (result?.type === 'error') {
      console.log('Authentication error:', result.params);
    } else {
      console.log('Authentication failed or cancelled:', result);
    }
  };

  return (
    <Pressable
      disabled={!request}
      onPress={handleClick}
      style={[
        componentStyles.pressable,
        code && { backgroundColor: 'transparent' }
      ]}
    >
      {({ hovered }) => (
        <View style={componentStyles.container}>
          {/* Animated gradient background for code indicator */}
          {code && (
            <Animated.View
              style={{
                ...StyleSheet.absoluteFillObject,
                opacity: fadeAnim,
                borderRadius: 10,
              }}
            >
              <LinearGradient
                colors={['#E4D337', '#FF5733']}
                style={{ flex: 1, borderRadius: 10 }}
              />
            </Animated.View>
          )}

          {/* Show a gradient and welcome message after successful login */}
          {userInfo && (
            <LinearGradient
              colors={['#0A66C2', '#004182']}
              style={StyleSheet.absoluteFillObject}
            />
          )}

          <View style={[
            componentStyles.innerView,
            {
              backgroundColor: code
                ? (hovered ? 'rgba(90,192,234,0.3)' : 'transparent')
                : (hovered ? 'rgba(90,192,234,0.5)' : 'rgba(255, 255, 255, 1)')
            }
          ]}>
            <View style={componentStyles.iconContainer}>
              <LottieView
              autoPlay={false}
                style={componentStyles.lottieIcon}
                source={require("@assets/animations/69066-linkedin-subtle.json")} // Ensure this path is correct
              />
            </View>
            <View style={componentStyles.textContainer}>
              <Text style={[componentStyles.text, { color: 'black' }]}>
                {userInfo ? `Welcome, ${userInfo.given_name}!` : 'Sign in with LinkedIn'}
              </Text>
            </View>
          </View>
        </View>
      )}
    </Pressable>
  );
}

const componentStyles = StyleSheet.create({
  pressable: {
    minHeight: 50,
    height: 50,
    maxHeight: 50,
    flex: 1,
    minWidth: "100%",
    alignItems: "stretch",
    backgroundColor: 'white',
    borderRadius: 30,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#E0E0E0',
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.2,
        shadowRadius: 2,
      },
    }),
  },
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  innerView: {
    flex: 1,
    width: '100%',
    paddingHorizontal: '10%',
    flexDirection: 'row',
    alignItems: 'center',
  },
  iconContainer: {
    flex: 1,
    maxWidth: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  lottieIcon: {
    width: 35,
    height: 35,
  },
  textContainer: {
    flex: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },
  text: {
    fontWeight: '600',
  },
});

export default LinkedInButton;
